// DLC_10 "Obliczenia techniczne" — API contract types (prefix /api/v1/tech-calc/).
// Every endpoint returns the standard { data, errors, meta } envelope; api.ts unwraps it.

// ── projects + variants ──────────────────────────────────────────────────────
export interface TechCalcVariant {
  id: string;
  name: string;
  /** Multiplier applied to access-line (przyłącza) loads during computation.
   *  DRF serializes DecimalField as a string — accept both. */
  access_lines_coeff: number | string;
  poles_count?: number;
  spans_count?: number;
}

export interface TechCalcProject {
  id: string;
  name: string;
  description: string;
  /** Author (created_by.get_full_name); null when the project has no author. */
  created_by_name?: string | null;
  /** Present on the list serializer (used by the projects table). */
  variants_count?: number;
  created_at: string;
  updated_at: string;
}

export interface TechCalcProjectDetail extends TechCalcProject {
  variants: TechCalcVariant[];
}

// ── drawing (GeoJSON FeatureCollection, WGS84 / EPSG:4326) ───────────────────
/** "pole" = słup, "access" = przyłącze abonenckie. */
export type NodeKind = "pole" | "access";

export interface PoleParams {
  pole_type: string;
  construction: string;
  material: string;
  /** Free text, e.g. "10.0". */
  height: string;
  function: string;
  /** Nr słupa (pełny). Optional: absent in drawings saved before the field existed. */
  full_number?: string;
  /** Nr historyczny. */
  historical_number?: string;
  /** Słup spoza opracowania: rysowany i obciąża sąsiadów przez swoje przęsła,
   *  ale sam nie jest liczony (brak wpisu w results.poles). Default false. */
  out_of_scope?: boolean;
  /** Słup oświetleniowy: silnik dolicza stałą siłę od oprawy Fl = 22 daN do każdej
   *  osi, której wzór wymienia Fl. Bez flagi Fl = 0 (jak w legacy). Default false. */
  is_lighting?: boolean;
}

/** Reverse-geocoded address (OSM) — read-only from the backend; save-drawing must
 *  pass it back 1:1 (never wipe an already-fetched address on the client). */
export interface PoleAddress {
  street: string | null;
  house_number: string | null;
  city: string | null;
  postcode: string | null;
  /** ISO timestamp of the OSM lookup; null = never fetched. */
  fetched_at: string | null;
}

export interface TechCalcPointProperties {
  /** Auto-assigned S1, S2… (poles) / A1, A2… (access lines). */
  ref: string;
  kind: NodeKind;
  label: string;
  params: PoleParams;
  address?: PoleAddress;
}

export interface FetchAddressesResult {
  updated: number;
  skipped: number;
  errors: string[];
}

export interface ConductorBundle {
  family: string; // "AL" | "AFL" | "AsXSn" (driven by dictionaries.families)
  cross_section: number;
  count: number; // liczba żył
}

export interface TechCalcLineProperties {
  from_ref: string;
  to_ref: string;
  bundles: ConductorBundle[];
  /** Manual length override in meters; null = use geometric length. */
  length_override: number | null;
  /** Geometric length in meters (recomputed on the client on every geometry change). */
  length_m: number;
}

export interface TechCalcPointFeature {
  type: "Feature";
  geometry: { type: "Point"; coordinates: [number, number] };
  properties: TechCalcPointProperties;
}

export interface TechCalcLineFeature {
  type: "Feature";
  geometry: { type: "LineString"; coordinates: [number, number][] };
  properties: TechCalcLineProperties;
}

export type TechCalcFeature = TechCalcPointFeature | TechCalcLineFeature;

export interface TechCalcDrawing {
  type: "FeatureCollection";
  features: TechCalcFeature[];
}

export const isPointFeature = (f: TechCalcFeature): f is TechCalcPointFeature =>
  f.geometry.type === "Point";
export const isLineFeature = (f: TechCalcFeature): f is TechCalcLineFeature =>
  f.geometry.type === "LineString";

// ── computation results ──────────────────────────────────────────────────────
export interface PoleResult {
  /** False = the pole could not be computed (see error_type / error). */
  ok: boolean;
  target_force_x: number | null;
  target_force_y: number | null;
  max_allowed_target_force_x: number | null;
  max_allowed_target_force_y: number | null;
  passed_x: boolean;
  passed_y: boolean;
  passed: boolean;
  /** Human-readable formula lines (mono display). */
  result_x_description: string[];
  result_y_description: string[];
  /** Przebieg obliczeń z podstawionymi wartościami: wzór, wartości składników,
   *  wykonane działanie. Brak w wynikach policzonych przed wprowadzeniem pola —
   *  wtedy zostaje sam wzór z `result_*_description` (patrz calcSteps w utils). */
  result_x_steps?: string[];
  result_y_steps?: string[];
  error_type?: string;
  /** Polish error message shown verbatim. */
  error?: string;
}

export interface VariantResults {
  results: { case_id: string; poles: Record<string, PoleResult> } | null;
  computed_at: string | null;
  catalog_version_at_compute: number | string | null;
  /** True when the drawing changed after the last compute — results are outdated. */
  stale: boolean;
}

// ── dictionaries ─────────────────────────────────────────────────────────────
export interface PoleTypeOption {
  value: string;
  /** False = the norm catalog has no entry for this type — selectable types only. */
  resolvable: boolean;
}

export interface TechCalcDictionaries {
  materials: string[];
  /** Flat list, {group: values} map, or [{key, values}] groups — normalized in utils. */
  constructions: string[] | Record<string, string[]> | { key: string; values: string[] }[];
  functions: string[];
  families: string[];
  cross_sections: number[];
  /** Either bare resolvable values or full options — normalized in utils. */
  pole_types: string[] | PoleTypeOption[];
  /** Full option list incl. non-resolvable types (preferred when present). */
  pole_types_all?: PoleTypeOption[];
}

// ── norm catalog ─────────────────────────────────────────────────────────────
// The contract fixes the endpoints but not the row field names (backend built in
// parallel), so rows are open records: the tables derive columns from the data —
// recognized keys get Polish labels, the rest are humanized (see utils.ts).
export type NormRow = { id: string } & Record<string, unknown>;
export type NormChange = { id?: string } & Record<string, unknown>;

// ── import trasy z projektu ZE (DLC_01) ──────────────────────────────────────
// Źródła wyboru dla formularza „Wczytaj trasę z projektu ZE" — projekty ZE, ich
// warianty tras i wersje. Endpoint wymaga dostępu do DLC_01 (poza modułem DLC_10),
// więc cała opcja jest widoczna tylko dla osób z tym dostępem.
export interface RouteImportVersion {
  id: string;
  number: number;
  is_current: boolean;
  /** "A3" — wariant + numer wersji, tak jak w projektach ZE. */
  label: string;
  length_m: number | null;
  created_at: string;
  comment: string;
}

export interface RouteImportRouteVariant {
  id: string;
  label: string;
  is_binding: boolean;
  /** Najnowsza wersja pierwsza (ordering wersji trasy). */
  versions: RouteImportVersion[];
}

export interface RouteImportSource {
  connection_id: string;
  project_id: string;
  name: string;
  client_name: string;
  variants: RouteImportRouteVariant[];
}

/** "overhead" = tylko linie napowietrzne (domyślnie), "all" = cała trasa. */
export type RouteImportScope = "overhead" | "all";

export interface RouteImportPayload {
  route_variant: string;
  route_version?: string;
  name?: string;
  line_scope: RouteImportScope;
  bundle: ConductorBundle;
}

/** meta.summary importu — co dokładnie wpadło do rysunku i co pominięto. */
export interface RouteImportSummary {
  variant_id: string;
  poles: number;
  access_points: number;
  spans: number;
  length_m: number;
  matched_objects: number;
  skipped_lines: { not_overhead: number; bad_geometry: number };
  line_scope: RouteImportScope;
  bundle: ConductorBundle;
  source: {
    connection_id: string;
    project_name: string;
    route_variant: string;
    route_version: number;
    route_version_id: string;
  };
  warnings: string[];
}

export interface RouteImportResult {
  variant: TechCalcVariant;
  summary: RouteImportSummary;
}
